import React from 'react'
import { ClientSideTable } from '../Table'
import { useLang } from '_metronic/i18n/Metronici18n'
import { isMobileDevice } from '_metronic/assets/ts/_utils'

import ProductCell from './ProductCell'
import QuantityCell from './QuantityCell'
import PriceCell from './PriceCell'

interface Props {
	code: string
	status: string
	items: {
		product: {
			name: string
			description: string
			featurePicturePath: string
			price: number
		}
		quantity: number
	}[]
	buyer: {
		featurePicturePath: string
		fullnameTh: string
		fullnameEn: string
		email: string
		uuId: string
	}
	onChat?: () => void
	onViewDetail?: () => void
}

const KumopackMarketplaceCardOverview: React.FC<Props> = ({
	code,
	status,
	buyer,
	items,
	onChat,
	onViewDetail,
}) => {
	const lang = useLang()
	let badgeStatusClass = 'btn btn-sm border-radius-16px px-2 py-1 '
	let badgeStatusText = ''
	let statusClass = 'fw-bold fs-5 '
	let statusText = ''

	const columns: any[] = [
		{
			Header: 'สินค้า',
			accessor: 'product',
			minWidth: !isMobileDevice() ? 600 : 150,
			width: !isMobileDevice() ? 600 : 150,
			Cell: ProductCell,
		},
		{
			headerRight: true,
			Header: 'จำนวน',
			accessor: 'quantity',
			width: 140,
			minWidth: 140,
			Cell: QuantityCell,
		},
		{
			headerRight: true,
			Header: 'ยอดคำสั่งซื้อ',
			accessor: 'price',
			width: 140,
			minWidth: 140,
			Cell: PriceCell,
		},
	]

	// Pending Payment
	if (status === '10') {
		badgeStatusClass += 'btn-kumopack-secondary-100 text-kumopack-secondary-600'
		badgeStatusText = lang === 'en' ? 'Pending Payment' : 'รอชำระเงิน'

		statusClass += 'text-kumopack-secondary-600'
		statusText = lang === 'en' ? 'Pending Payment' : 'รอชำระเงิน'
	}
	// Pending Delivery
	else if (status === '20') {
		badgeStatusClass += 'btn-kumopack-secondary-100 text-kumopack-secondary-600'
		badgeStatusText = lang === 'en' ? 'Pending Delivery' : 'รอจัดส่ง'

		statusClass += 'text-kumopack-secondary-600'
		statusText += lang === 'en' ? 'Pending Delivery' : 'รอจัดส่ง'
	}
	// On Deliver
	else if (status === '30') {
		badgeStatusClass += 'btn-kumopack-warning-100 text-kumopack-warning-600'
		badgeStatusText = lang === 'en' ? 'On Deliver' : 'การจัดส่ง'

		statusClass += 'text-kumopack-warning-600'
		statusText += lang === 'en' ? 'On Deliver' : 'การจัดส่ง'
	}
	// Delivered
	else if (status === '40') {
		badgeStatusClass += 'btn-kumopack-success-100 text-kumopack-success-600'
		badgeStatusText = lang === 'en' ? 'Delivered' : 'จัดส่งแล้ว'

		statusClass += 'text-kumopack-success-600'
		statusText += lang === 'en' ? 'Delivered' : 'จัดส่งแล้ว'
	}
	// Cancelled
	else if (status === '50') {
		badgeStatusClass += 'btn-kumopack-error-100 text-kumopack-error-600'
		badgeStatusText = lang === 'en' ? 'Cancelled' : 'ยกเลิก'

		statusClass += 'text-kumopack-error-600'
		statusText = lang === 'en' ? 'Cancelled' : 'ยกเลิก'
	}
	// Refund/Return
	else if (status === '60') {
		badgeStatusClass += 'btn-kumopack-primary-100 text-kumopack-primary-600'
		badgeStatusText = lang === 'en' ? 'Refund/Return' : 'การคืนเงิน/สินค้า'

		statusClass += 'text-kumopack-primary-600'
		statusText = lang === 'en' ? 'Refund/Return' : 'การคืนเงิน/สินค้า'
	}
	// Delivery Failed
	else if (status === '70') {
		badgeStatusClass += 'btn-kumopack-error-100 text-kumopack-error-600'
		badgeStatusText = lang === 'en' ? 'Delivery Failed' : 'จัดส่งไม่สำเร็จ'

		statusClass += 'text-kumopack-error-600'
		statusText = lang === 'en' ? 'Delivery Failed' : 'จัดส่งไม่สำเร็จ'
	}

	return (
		<React.Fragment>
			<div className="card border border-kumopack-grey-200">
				<div className="card-header">
					<div className="d-flex flex-column flex-xl-row align-items-start align-items-xl-center justify-content-between w-100 my-5">
						{/* Title */}
						<div>
							<div className="d-flex flex-row align-items-center" style={{ columnGap: '12px' }}>
								<h4 className="text-kumopack-grey-900 fw-bold mb-0 hover-enabled cursor-pointer">
									{lang === 'en' ? 'Code' : 'หมายเลขคำสั่งซื้อ'}:{' '}
									{String(code).length === 0 || !code ? '-' : code}
								</h4>
								<div className={badgeStatusClass}>{badgeStatusText}</div>
							</div>
						</div>

						{/* Status */}
						<div
							className="d-flex flex-row align-items-center mt-5 mt-xl-0"
							style={{ columnGap: '12px' }}>
							<span className="w-8px h-8px bullet bullet-dot bg-kumopack-success-500" />
							<span className={statusClass}>สถานะ: {statusText}</span>
							<button
								className="btn btn-sm btn-kumopack-secondary-600 text-kumopack-base-white fw-bold"
								onClick={onViewDetail}>
								<img
									className="me-2"
									src="/media/icons/kumopack/white-file-search-02.svg"
									alt="View Detail"
								/>
								<span>{lang === 'en' ? 'View Detail' : 'ตรวจสอบรายละเอียด'}</span>
							</button>
						</div>
					</div>
				</div>

				<div className="card-body p-0">
					<ClientSideTable isGrey data={items} columns={columns} items_per_page={9999} />

					<hr
						style={{
							background: 'transparent',
							borderBottom: '1px solid #CCC',
							borderTop: 0,
							borderLeft: 0,
							borderRight: 0,
							margin: 0,
						}}
					/>

					<div className="d-flex flex-column flex-lg-row justify-content-lg-between p-5">
						<div className="d-flex flex-row align-items-center" style={{ gap: '12px' }}>
							<img
								className="rounded-circle h-30px w-30px object-fit-cover"
								src={buyer.featurePicturePath}
								alt={buyer.fullnameEn}
							/>
							<div>
								<div className="text-kumopack-grey-900 fw-bold">
									{lang === 'en' ? buyer.fullnameEn : buyer.fullnameTh}
								</div>
								<div className="text-kumopack-grey-600">{buyer.email}</div>
							</div>
							<button className="btn btn-sm btn-muted btn-active-light px-2" onClick={onChat}>
								<img src="/media/icons/kumopack/annotation-dots.svg" alt="Chat" />
							</button>
							<button
								className="btn btn-sm btn-kumopack-success-600 text-kumopack-base-white px-2"
								onClick={onChat}>
								<img src="/media/icons/kumopack/Bus.svg" alt="Shipping Car" />
								<span className="text-kumopack-base-white ms-1 fw-bold">ยืนยันการจัดส่ง</span>
							</button>
						</div>

						<div className="d-flex flex-row align-items-center" style={{ gap: '12px' }}>
							<div className="text-kumopack-grey-600">ช่องทางการจัดส่ง:</div>
							<img src="/media/icons/kumopack/kerry.svg" alt="Kerry Express" />
							<div>
								<div className="text-kumopack-grey-900 fw-bold">Kerry Express</div>
								<div className="text-kumopack-grey-600">SHNV311984037</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</React.Fragment>
	)
}

export default KumopackMarketplaceCardOverview
